import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import { MdLocalPhone, MdPerson, MdHome, MdEmail, MdLock } from "react-icons/md";
import { LuBuilding } from "react-icons/lu";
import SoftButton from "../../components/SoftButton";
import {getDepartementsList, addLibrarian, getUniversities, addLibrarianAdmin} from "../../axios-client";
import { IoClose } from "react-icons/io5";
import Divider from '@mui/material/Divider';
import PropTypes from "prop-types";
import CircularProgress from '@mui/material/CircularProgress';
import {FaUniversity} from "react-icons/fa";
import {getPasswordPattern} from "../../utlis/generalUtils";
import i18n from "config/i18n";
import {ALMANDUMA_CUSTOMER} from "../../utlis/constant";

const AddLibrarianDialog = ({ open, onClose, onLibrarianAdded, isSuperAdmin, setSnackbar, t }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        department: '',
        university:'',
        password: "",
        address: "",
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [departments, setDepartments] = useState([]);
    const [universities, setUniversities] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const customerName = window?.appConfig?.REACT_APP_CUSTOMER_NAME || process.env.REACT_APP_CUSTOMER_NAME

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    useEffect(() => {

        if (isSuperAdmin) {
            getUniversities().then(response => {
                setUniversities(response.data.universities || []);
                setDepartments([]);
            }).catch(error => {
                setUniversities([]);
                setDepartments([]);
            });

        } else {
            getDepartementsList().then(response => {
                const departmentsList = response.data.departments || [];

                setDepartments(departmentsList);

                if (departmentsList.length > 0) {

                    setFormData(prev => ({
                        ...prev,
                        department: departmentsList[0].id
                    }));
                }

                setUniversities([]);

            }).catch(error => {
                setUniversities([]);
                setDepartments([]);
            });
        }
    }, [isSuperAdmin])

    const handleDepartmentChange = (e) => {
        setFormData({ ...formData, department: e.target.value });
    };
    const handleUniversityChange = (e) => {
        setFormData({ ...formData, university: e.target.value });
    };

    const validateForm = () => {
        let formErrors = {};
        if (!formData.name) formErrors.name = t("dialogs.AddNewLibrarian.messages.NameRequired");
        if (!formData.email) formErrors.email = t("dialogs.AddNewLibrarian.messages.EmailRequired");
        if (!isSuperAdmin) {
            if (!formData.department) formErrors.department = t("dialogs.AddNewLibrarian.messages.DepartmentRequired");
        } else {
            if (!formData.university) formErrors.university = t("dialogs.AddNewLibrarian.messages.UniversityRequired");
        }
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailPattern.test(formData.email)) {
            formErrors.email = t("dialogs.AddNewLibrarian.messages.InvalidEmail");
        }

        const password = formData.password ? formData.password.trim() : "";
        if (!password) {
            formErrors.password = t("dialogs.AddNewLibrarian.messages.PasswordRequired");
        } else {
            const passwordPattern = getPasswordPattern();
            if (!passwordPattern.test(password)) {
                formErrors.password =  t("dialogs.AddNewLibrarian.messages.InvalidPassword");
            }
        }

        return formErrors;
    };

    const handleSubmit = async () => {
        const formErrors = validateForm();
        if (Object.keys(formErrors).length === 0) {
            setIsCreating(true)
            try {
                const action = isSuperAdmin ? addLibrarianAdmin : addLibrarian;
                await action(formData);
                setIsCreating(false)
                onLibrarianAdded();
                setSnackbar({ open: true, message: t(isSuperAdmin ? "dialogs.AddNewLibrarian.messages.LibrarianAdminAdded" : customerName === ALMANDUMA_CUSTOMER ? "dialogs.AddNewLibrarian.messages.UserAdded" : "dialogs.AddNewLibrarian.messages.LibrarianAdded"), severity: 'success' });
                onClose();
            } catch (error) {
                setIsCreating(false)
                setSnackbar({ open: true, message: t("dialogs.AddNewLibrarian.messages.GenericError"), severity: 'error' });
            }
        } else {
            setErrors(formErrors);
            setSnackbar({ open: true, message: t("dialogs.AddNewLibrarian.messages.FormIncomplete"), severity: 'error' });
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const handleSnackbarClose = () => {
        setSnackbar({ open: false, message: '', severity: '' });
    };
    const inputContainerStyle = {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${isFocused ? '#cdcecf' : '#dcdfe3'}`,
        borderRadius: '8px',
        padding: '5px 10px',
        marginBottom: '10px',
        transition: 'border-color 0.3s ease',
    };

    const inputStyle = {
        width: '100%',
        padding: '10px 0px',
        border: 'none',
        fontSize: '14px',
        boxSizing: 'border-box',
        outline: 'none',
        fontFamily: 'Arial, Roboto',
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <DialogTitle
                id="form-dialog-title"
                sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
            >
                { isSuperAdmin ? "Add New Librarian Admin": customerName === ALMANDUMA_CUSTOMER ? t('dialogs.AddNewLibrarian.userTitle') : t('dialogs.AddNewLibrarian.title')}
                <IoClose
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        size: 10
                    }}
                />
            </DialogTitle>
            <Divider/>
            <DialogContent>
                <label htmlFor="name" className="label-style">{t('dialogs.AddNewLibrarian.Name')}</label>
                <div style={inputContainerStyle}>
                    <MdPerson style={{...(i18n.language === "ar" ? {marginLeft: "8px"} : {marginRight: "8px"}), color: "#888"}}/>
                    <input type="text" id="name" name="name" style={inputStyle}
                           value={formData.name}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.name && <p style={{color: 'red', fontSize: "12px"}}>{errors.name}</p>}

                <label htmlFor="email" className="label-style">{t('dialogs.AddNewLibrarian.Email')}</label>
                <div style={inputContainerStyle}>
                    <MdEmail style={{...(i18n.language === "ar" ? {marginLeft: "8px"} : {marginRight: "8px"}), color: "#888"}}/>
                    <input type="text" id="email" name="email" style={inputStyle}
                           value={formData.email}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.email && <p style={{color: 'red', fontSize: "12px"}}>{errors.email}</p>}

                <label htmlFor="password" className="label-style">{t('dialogs.AddNewLibrarian.Password')}</label>
                <div style={{ ...inputContainerStyle, position: 'relative' }}>
                    <MdLock style={{...(i18n.language === "ar" ? {marginLeft: "8px"} : {marginRight: "8px"}), color: "#888"}}/>
                    <input type={showPassword ? "text" : "password"}
                           id="password" name="password" style={inputStyle}
                           value={formData.password}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                    <IconButton
                        onClick={togglePasswordVisibility}
                        sx={{
                            position: "absolute",
                            top: "50%",
                            transform: "translateY(-50%)",
                            padding: "5px",
                            ...(i18n.language === "ar" ? {left:8} : {right:8}),
                        }}
                        aria-label="toggle password visibility"
                    >
                        {showPassword ? <Visibility/> : <VisibilityOff/>}
                    </IconButton>
                </div>
                {errors.password && <p style={{color: 'red', fontSize: "12px"}}>{errors.password}</p>}

                <label htmlFor="phone" className="label-style">{t('dialogs.AddNewLibrarian.Phone')}</label>
                <div style={inputContainerStyle}>
                    <MdLocalPhone style={{...(i18n.language === "ar" ? {marginLeft: "8px"} : {marginRight: "8px"}), color: "#888"}}/>
                    <input type="text" id="phone" name="phone" style={inputStyle}
                           value={formData.phone}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.phone && <p style={{color: 'red', fontSize: "12px"}}>{errors.phone}</p>}

                {isSuperAdmin ? (
                    <>
                        <label htmlFor="university" className="label-style">University</label>

                        <SoftBox mb={2} style={{ position: 'relative' }}>
                            <FaUniversity className="icon-style" style={{
                                position: 'absolute',
                                left: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none',
                                fontSize: '20px',
                                color: '#666'
                            }} />

                            <select
                                id="university"
                                name="university"
                                value={formData.university}
                                onChange={handleUniversityChange}
                                style={{
                                    width: "100%",
                                    ...(i18n.language === "ar" ? {padding: "10px 40px 10px 10px"} : {padding: "10px 10px 10px 40px"}),
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                                    background: "transparent",
                                }}
                            >
                                <option disabled={true} value="">
                                    Select a university
                                </option>
                                {universities?.map((university) => (
                                    <option key={university.id} value={university.id}>
                                        {university.name}
                                    </option>
                                ))}
                            </select>
                            {errors.university && (
                                <SoftTypography color="error" fontSize="small">
                                    {errors.university}
                                </SoftTypography>
                            )}
                        </SoftBox>
                    </>

                ) : (
                    <>
                        <label htmlFor="department" className="label-style">{t('dialogs.AddNewLibrarian.Department')}</label>

                        <SoftBox mb={2} style={{ position: 'relative' }}>
                            <LuBuilding className="icon-style" style={{
                                position: 'absolute',
                              ...(i18n.language === "ar" ? {right: "10px"} : {left: "10px"}),
                                top: '50%',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none',
                                fontSize: '20px',
                                color: '#666'
                            }} />

                            <select
                                id="department"
                                name="department"
                                value={formData.department}
                                onChange={handleDepartmentChange}
                                style={{
                                    width: "100%",
                                    ...(i18n.language === "ar" ? {padding: "10px 40px 10px 10px"} : {padding: "10px 10px 10px 40px"}),
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                                    background: "transparent",
                                }}
                            >
                                <option disabled={true} value="">
                                    Select a department
                                </option>
                                {departments?.map((department) => (
                                    <option key={department.id} value={department.id}>
                                        {department.name}
                                    </option>
                                ))}
                            </select>
                            {errors.department && (
                                <SoftTypography color="error" fontSize="small">
                                    {errors.department}
                                </SoftTypography>
                            )}
                        </SoftBox>
                    </>

                )}



                <label htmlFor="address" className="label-style">{t('dialogs.AddNewLibrarian.Address')}</label>
                <div style={inputContainerStyle}>
                    <MdHome style={{...(i18n.language === "ar" ? {marginLeft: "8px"} : {marginRight: "8px"}), color: "#888"}}/>
                    <input type="text" id="address" name="address" style={inputStyle}
                           value={formData.address}
                           onChange={handleChange}
                           onFocus={() => setIsFocused(true)}
                           onBlur={() => setIsFocused(false)}/>
                </div>
                {errors.address && <p style={{color: 'red', fontSize: "12px"}}>{errors.address}</p>}

            </DialogContent>

            <DialogActions>
                <SoftButton variant="outlined" color="dark" onClick={handleCancel}>
                    {t('dialogs.AddNewLibrarian.ActionButtons.Cancel')}
                </SoftButton>
                <SoftButton variant="gradient" color="primary" onClick={handleSubmit} type="submit" sx={{ml: 1 , mr:  (i18n.language === 'ar' ? 1 : 0)}} >
                    {isCreating ? t('dialogs.AddNewLibrarian.ActionButtons.PleaseWait') : t('dialogs.AddNewLibrarian.ActionButtons.Submit')}
                    {isCreating && <CircularProgress size={20} style={{ marginLeft: 10, color: "white" }} />}
                </SoftButton>
            </DialogActions>
        </Dialog>
    );
};

AddLibrarianDialog.propTypes = {

    open: PropTypes.bool,
    onClose: PropTypes.func,
    onLibrarianAdded: PropTypes.func,
    isSuperAdmin: PropTypes.bool.isRequired,
    setSnackbar : PropTypes.func,
    t : PropTypes.object
}

export default AddLibrarianDialog;
